import { useLayoutEffect, useRef, useState } from 'react'

const ELLIPSIS = '…'

/** Сколько строк занимает текст объявления. */
type Lines = 1 | 2

const CLAMP: Record<Lines, string> = { 1: 'line-clamp-1', 2: 'line-clamp-2' }

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** Смещение элемента внутри текста строки. */
const getTextOffset = (root: HTMLElement, mark: HTMLElement) => {
  const range = document.createRange()
  range.setStart(root, 0)
  range.setEndBefore(mark)

  return range.toString().length
}

/** Скрытая копия строки: те же шрифт и стили, что у видимого текста, плюс стили замера. */
const createProbe = (node: HTMLElement, className: string, style: string) => {
  const probe = node.cloneNode(false) as HTMLElement

  probe.className = className
  probe.style.cssText = `position:absolute;top:0;left:-9999px;visibility:hidden;pointer-events:none;min-width:0;${style}`
  ;(node.parentElement ?? document.body).appendChild(probe)

  return probe
}

/** Наибольшее значение из [lo, hi], на котором предикат ещё выполняется. */
const findMax = (lo: number, hi: number, fits: (value: number) => boolean) => {
  while (lo < hi) {
    const middle = Math.ceil((lo + hi) / 2)

    if (fits(middle)) {
      lo = middle
    } else {
      hi = middle - 1
    }
  }

  return lo
}

/**
 * Фрагмент вокруг вхождения ТМ, максимально расширенный в обе стороны так,
 * чтобы уместиться в одну строку шириной maxWidth. Обрезанные стороны помечаются многоточием.
 */
const buildSnippet = (
  measure: (html: string) => number,
  text: string,
  markStart: number,
  markEnd: number,
  maxWidth: number,
) => {
  const toHtml = (from: number, to: number) => {
    const markedEnd = Math.min(to, markEnd)

    return (
      escapeHtml((from > 0 ? ELLIPSIS : '') + text.slice(from, markStart)) +
      `<mark>${escapeHtml(text.slice(markStart, markedEnd))}</mark>` +
      escapeHtml(text.slice(markedEnd, to) + (to < text.length ? ELLIPSIS : ''))
    )
  }

  const fits = (from: number, to: number) => measure(toHtml(from, to)) <= maxWidth

  // само вхождение шире строки — обрезаем его справа
  if (!fits(markStart, markEnd)) {
    return toHtml(
      markStart,
      findMax(markStart + 1, markEnd, (to) => fits(markStart, to)),
    )
  }

  // симметрично расширяем контекст вокруг вхождения
  const startAt = (context: number) => Math.max(0, markStart - context)
  const endAt = (context: number) => Math.min(text.length, markEnd + context)
  const context = findMax(0, Math.max(markStart, text.length - markEnd), (value) =>
    fits(startAt(value), endAt(value)),
  )

  let from = startAt(context)
  let to = endAt(context)

  // одна из сторон упёрлась в край текста — добираем остаток на другой
  if (from === 0) {
    to = findMax(to, text.length, (value) => fits(0, value))
  } else if (to === text.length) {
    from -= findMax(0, from, (value) => fits(from - value, text.length))
  }

  return toHtml(from, to)
}

/** Видно ли вхождение ТМ целиком в строке, обрезанной до lines строк. */
const hasVisibleMark = (probe: HTMLElement, lines: Lines, ellipsisWidth: number) => {
  const probeRect = probe.getBoundingClientRect()
  const isClipped = probe.scrollHeight > probe.clientHeight
  // у обрезанного текста многоточие съедает конец последней видимой строки
  const lastLineTop = probeRect.bottom - probeRect.height / lines
  const visibleRight = probeRect.right - ellipsisWidth

  const isVisible = (rect: DOMRect) =>
    rect.bottom <= probeRect.bottom + 0.5 &&
    (!isClipped || rect.top < lastLineTop - 0.5 || rect.right <= visibleRight + 0.5)

  return Array.from(probe.querySelectorAll('mark')).some((mark) =>
    Array.from(mark.getClientRects()).every(isVisible),
  )
}

interface AdLineView {
  lines: Lines
  /** Разметка дополнительной строки с вхождением ТМ. */
  snippet: string | null
}

const ONE_LINE: AdLineView = { lines: 1, snippet: null }
const TWO_LINES: AdLineView = { lines: 2, snippet: null }

/**
 * Минимальная раскладка, в которой видно вхождение ТМ: одна строка, две строки
 * либо первая строка плюс отдельная строка с фрагментом вокруг вхождения.
 * null — строку ещё нельзя измерить.
 */
const getAdLineView = (node: HTMLElement, className: string, html: string): AdLineView | null => {
  const width = node.getBoundingClientRect().width

  if (!width) {
    return null
  }

  const clampProbe = createProbe(node, className, `width:${width}px`)
  const ruler = createProbe(
    node,
    className,
    'width:auto;max-width:none;white-space:pre;text-overflow:clip',
  )

  const measure = (value: string) => {
    ruler.innerHTML = value

    return ruler.getBoundingClientRect().width
  }

  try {
    clampProbe.innerHTML = html

    const mark = clampProbe.querySelector('mark')

    if (!mark?.textContent) {
      return ONE_LINE
    }

    const ellipsisWidth = measure(ELLIPSIS)
    const fitsIn = (lines: Lines) => {
      clampProbe.className = `${className} ${CLAMP[lines]}`

      return hasVisibleMark(clampProbe, lines, ellipsisWidth)
    }

    if (fitsIn(1)) {
      return ONE_LINE
    }

    if (fitsIn(2)) {
      return TWO_LINES
    }

    const markStart = getTextOffset(clampProbe, mark)

    return {
      lines: 1,
      snippet: buildSnippet(
        measure,
        clampProbe.textContent ?? '',
        markStart,
        markStart + mark.textContent.length,
        width,
      ),
    }
  } finally {
    clampProbe.remove()
    ruler.remove()
  }
}

interface MarkedAdLineProps {
  html: string
  className?: string
}

/**
 * Текст объявления, обрезанный до минимума, в котором видно вхождение ТМ (<mark>):
 * одна строка, если вхождение попадает в неё; две строки, если попадает во вторую;
 * иначе первая строка с многоточием и под ней строка с фрагментом вокруг вхождения,
 * обрезанным с одной или двух сторон.
 */
export const MarkedAdLine = ({ html, className = '' }: MarkedAdLineProps) => {
  const lineRef = useRef<HTMLParagraphElement>(null)
  const [view, setView] = useState<AdLineView>(ONE_LINE)

  useLayoutEffect(() => {
    const node = lineRef.current

    if (!node) {
      return
    }

    let disposed = false

    const update = () => {
      const next = disposed ? null : getAdLineView(node, className, html)

      if (next) {
        setView((prev) =>
          prev.lines === next.lines && prev.snippet === next.snippet ? prev : next,
        )
      }
    }

    update()
    // замер зависит от шрифта, поэтому пересчитываем после его загрузки
    document.fonts?.ready.then(update)

    const observer = new ResizeObserver(update)
    observer.observe(node)

    return () => {
      disposed = true
      observer.disconnect()
    }
  }, [html, className])

  return (
    <>
      <p
        ref={lineRef}
        className={`${className} ${CLAMP[view.lines]}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {view.snippet && (
        <p className={`${className} truncate`} dangerouslySetInnerHTML={{ __html: view.snippet }} />
      )}
    </>
  )
}
